import { request } from '../utils';

// Payment notes — at month end FINANCE issues a customer a request for payment
// and a statement of account covering the overdraft they drew that month.
//
// It is NOT a tax invoice and must never be called one anywhere in the UI. The
// document says so in its own footer. "Payment note" everywhere.
//
// Two operations, and the difference between them is the thing to get right:
//
//   Preview (GET)  — no side effects at all. Repeat it freely.
//   Issue  (POST)  — persists a snapshot AND emails the customer. One per
//                    customer per period, enforced by a database constraint, so
//                    it cannot be undone, redone or resent.

export const NOTES_MAX = 500;

// `callApi` resolves the envelope but throws the HTTP status away, and the
// issue endpoint needs it: 409 (already issued for this period) and both 422s
// (nothing to bill / no email on file) carry no discriminator in the body —
// only a message. Go through the shared axios instance so the maker-checker and
// 2FA response interceptors still run, and keep the status on the rejection.
const call = (endpoint, { params, body, method = 'get', config = {} } = {}) =>
  request({ url: endpoint, method, params, data: body, ...config })
    .then((res) => res.data)
    .catch((err) => {
      if (err && err.response) {
        const data = err.response.data;
        return Promise.reject({
          ...(data && typeof data === 'object'
            ? data
            : { message: String(data || 'Request failed.') }),
          httpStatus: err.response.status,
        });
      }

      if (err && err.isAxiosError) {
        return Promise.reject({
          message: err.message || 'Network Error. Please try again.',
          title: '❌ Network Error',
        });
      }

      // Already-shaped rejection from a response interceptor (approval-required
      // carrying __approvalHandled, or a cancelled 2FA prompt). Pass it through
      // untouched so showError can recognise it.
      return Promise.reject(err);
    });

const base = (userId) => `/users/${userId}/wallet/overdraft/payment-notes`;

/* -------------------------------------------------------------------------- */
/* Periods                                                                     */
/* -------------------------------------------------------------------------- */

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const pad = (n) => (n < 10 ? `0${n}` : String(n));

// A period is a calendar month, not an instant, so it is built from local date
// parts — converting through UTC would slide it into the wrong month at the
// boundaries.
export const toPeriod = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;

export const periodLabel = (period) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(period == null ? '' : period));
  if (!m) return String(period == null ? '—' : period);
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${m[1]}` : String(period);
};

// The default: the month before the one we are in.
export const lastCompleteMonth = (now = new Date()) => {
  const d = new Date(now.getFullYear(), now.getMonth(), 1);
  d.setMonth(d.getMonth() - 1);
  return toPeriod(d);
};

export const PERIOD_CHOICES = 12;

// Only *complete* months are offered. Excluding the month in progress is
// deliberate and goes beyond the server's "no future periods" rule: issuing is
// one-shot per period, so a note raised on the 3rd for the current month would
// permanently burn that period over a few days' activity, with no way to
// re-issue it properly at month end.
export const periodOptions = (now = new Date(), count = PERIOD_CHOICES) => {
  const out = [];
  const d = new Date(now.getFullYear(), now.getMonth(), 1);

  for (let i = 0; i < count; i += 1) {
    d.setMonth(d.getMonth() - 1);
    out.push(toPeriod(d));
  }

  return out;
};

/* -------------------------------------------------------------------------- */
/* Validation                                                                  */
/* -------------------------------------------------------------------------- */

// `YYYY-MM` sorts lexicographically, so a string compare is a date compare.
export const validatePeriod = (period, now = new Date()) => {
  const value = String(period == null ? '' : period).trim();

  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return 'Choose a month.';
  if (value >= toPeriod(now)) return 'Choose a month that has already ended.';

  return null;
};

// Optional — the server defaults it to 14 days from issue.
export const validateDueDate = (raw) => {
  const value = String(raw == null ? '' : raw).trim();
  if (!value) return null;

  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) return 'Enter a date as YYYY-MM-DD.';

  // Round-trip rather than trusting the parser: engines differ on whether an
  // impossible date like 2026-02-31 is Invalid Date or rolls over into March.
  const [, y, m, d] = parts.map(Number);
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return 'Enter a valid date.';
  }

  return null;
};

export const validateNotes = (raw) => {
  const value = String(raw == null ? '' : raw);
  if (value.length > NOTES_MAX) {
    return `Notes must be ${NOTES_MAX} characters or fewer.`;
  }
  return null;
};

// Shared with the set-limit form — the `400 validation-error` envelope is the
// same across the admin API.
export { toFieldErrors } from './overdraft.service';

/* -------------------------------------------------------------------------- */
/* Reading                                                                     */
/* -------------------------------------------------------------------------- */

// GET …/payment-notes/:period?format=json
//
// A preview. No side effects, nothing stored, nobody emailed — safe to re-run on
// every change of the period selector.
//
// `data.note.status` is "draft" until the period has been issued and "issued"
// afterwards, so this doubles as "has this month already been sent?".
export const previewPaymentNote = (userId, period) =>
  call(`${base(userId)}/${encodeURIComponent(period)}`, {
    params: { format: 'json' },
  }).then((res) => (res && res.data) || null);

// GET …/payment-notes/:period?format=html
//
// The rendered document as markup, not JSON — `transformResponse` is replaced so
// axios does not try to parse it. There is no PDF endpoint and we are not
// building a converter for one: the document is A4-styled and self-contained
// (no external CSS, fonts or images), so the browser's own print dialog is the
// PDF story.
export const fetchPaymentNoteHtml = (userId, period) =>
  call(`${base(userId)}/${encodeURIComponent(period)}`, {
    params: { format: 'html' },
    config: { responseType: 'text', transformResponse: [(d) => d] },
  });

// GET …/payment-notes — the issued history, newest first.
//
// Read `meta` carefully before rendering any of this: each row's `amount_due` is
// a frozen snapshot of what was requested at issue time, while
// `meta.outstanding_now` is the live wallet debt. There is exactly one overdraft
// balance, so the rows DO NOT sum to what is owed — see `PaymentNoteHistory`.
export const listPaymentNotes = (userId, { page = 1, limit = 30 } = {}) =>
  call(base(userId), { params: { page, limit } }).then(
    ({ data = [], meta = {} } = {}) => ({ rows: data, meta })
  );

/* -------------------------------------------------------------------------- */
/* Issuing                                                                     */
/* -------------------------------------------------------------------------- */

// POST …/payment-notes — persists the note and emails the customer.
export const issuePaymentNote = (userId, { period, dueAt, notes } = {}) => {
  const body = { period };

  const due = String(dueAt == null ? '' : dueAt).trim();
  if (due) body.due_at = due;

  const trimmed = String(notes == null ? '' : notes).trim();
  if (trimmed) body.notes = trimmed;

  return call(base(userId), { method: 'post', body });
};

export const ISSUE_OUTCOME = {
  SENT: 'SENT',
  NOT_EMAILED: 'NOT_EMAILED',
};

// A 201 is not automatically good news.
//
// The server records the note first and attempts the email after. If the send
// fails the record still exists — and because one note per customer per period
// is a database constraint, the period is then spent for good and there is no
// resend endpoint. So `meta.emailed === false` on a 201 has to read as a
// warning with a "go contact them yourself" instruction, never as a success.
export const issueOutcome = (res = {}) => {
  const meta = (res && res.meta) || {};
  const note = (res && res.data) || {};
  const emailed = meta.emailed === true;

  return {
    outcome: emailed ? ISSUE_OUTCOME.SENT : ISSUE_OUTCOME.NOT_EMAILED,
    emailed,
    emailedTo: meta.emailed_to || null,
    emailError: meta.email_error || null,
    message: (res && res.message) || '',
    note,
  };
};

/* -------------------------------------------------------------------------- */
/* Rejection classification                                                    */
/* -------------------------------------------------------------------------- */

// Maker-checker. Not an error: nothing was persisted and no email was sent — the
// operation is parked for a second approver. The response interceptor has
// already opened the approval prompt and flagged the rejection.
//
// The control is config-gated and off by default, so the identical request may
// simply return 201. Always branch on the response, never on the environment.
export const isApprovalRequired = (err) =>
  !!err &&
  typeof err === 'object' &&
  (err.__approvalHandled === true || err.status === 'approval-required');

// 409 — this period already has a note. Show the existing one; never retry.
export const isAlreadyIssued = (err) => !!err && err.httpStatus === 409;

// 400 — bad period, due date or notes, with a field-level `errors` array.
export const isValidationError = (err) =>
  !!err &&
  typeof err === 'object' &&
  (err.status === 'validation-error' || err.httpStatus === 400);

// 422 — "customer not found", "nothing to bill for this period", "customer has
// no email address". All operator-actionable statements rather than faults, so
// they belong in the dialog rather than in an error toast.
export const isUnprocessable = (err) => !!err && err.httpStatus === 422;

export default {
  previewPaymentNote,
  fetchPaymentNoteHtml,
  listPaymentNotes,
  issuePaymentNote,
};
